<template>
  <div class="page org">
    <div class="kpi-row">
      <div class="kpi" @click="openMembersKpi">
        <div class="kpi__left">
          <span class="kpi__icon-wrap"><i class="ri-group-fill kpi__icon" aria-hidden="true" /></span>
          <span class="kpi__label">会员总数</span>
        </div>
        <b>{{ kpiInt("members") }}<em>人</em></b>
      </div>
      <div class="kpi" @click="openFlowKpi">
        <div class="kpi__left">
          <span class="kpi__icon-wrap kpi__icon-wrap--party">
            <img class="kpi__party" :src="partyEmblem" alt="" />
          </span>
          <span class="kpi__label">流动党员数</span>
        </div>
        <b>{{ kpiInt("flow") }}<em>人</em></b>
      </div>
      <div class="kpi" @click="openNewMembersKpi">
        <div class="kpi__left">
          <span class="kpi__icon-wrap"><i class="ri-user-add-fill kpi__icon" aria-hidden="true" /></span>
          <span class="kpi__label">年新会员数</span>
        </div>
        <b>{{ kpiInt("newMembers") }}<em>人</em></b>
      </div>
      <div class="kpi" @click="openRepsKpi">
        <div class="kpi__left">
          <span class="kpi__icon-wrap"><i class="ri-vip-crown-fill kpi__icon" aria-hidden="true" /></span>
          <span class="kpi__label">代表人士数</span>
        </div>
        <b>{{ kpiInt("reps") }}<em>人</em></b>
      </div>
    </div>

    <div class="cols">
      <div class="col col--left">
        <section class="panel panel--chart">
          <h3>政治面貌</h3>
          <div ref="politicsChart" class="chart"></div>
        </section>

        <section class="panel panel--tree">
          <h3>组织架构</h3>
          <el-scrollbar class="tree-scroll">
            <ul class="tree">
              <li class="tree__root">
                <button type="button" class="tree__node" @click="toggleRoot">
                  <i :class="rootExpanded ? 'ri-arrow-down-s-line' : 'ri-arrow-right-s-line'" />
                  <strong>{{ orgName }}</strong>
                </button>
                <ul v-show="rootExpanded" class="tree__children">
                  <li v-for="b in visibleBranches" :key="'b-' + b.id" class="tree__branch">
                    <div class="tree__branch-row">
                      <button
                        type="button"
                        class="tree__toggle"
                        :aria-expanded="isBranchExpanded(b.id)"
                        @click.stop="toggleBranch(b.id)"
                      >
                        <i
                          :class="
                            isBranchExpanded(b.id)
                              ? 'ri-arrow-down-s-line'
                              : 'ri-arrow-right-s-line'
                          "
                        />
                      </button>
                      <button
                        type="button"
                        class="tree__node tree__node--branch"
                        :class="{ 'is-incomplete': isEmptyNode(b) }"
                        @click="onBranchClick(b)"
                      >
                        <i class="ri-building-line" />
                        <span>{{ b.name }}</span>
                        <em>（{{ b.memberCount || 0 }} 人）</em>
                        <span v-if="showIncompleteTip(b)" class="tree__todo">待完善</span>
                      </button>
                    </div>
                    <ul v-show="isBranchExpanded(b.id)" class="tree__children tree__children--committee">
                      <li
                        v-for="c in visibleCommitteesOf(b)"
                        :key="'c-' + b.id + '-' + c.id"
                        class="tree__committee"
                      >
                        <button
                          type="button"
                          class="tree__node tree__node--committee"
                          :class="{ 'is-incomplete': isEmptyNode(c) }"
                          @click="openCommittee(c, b)"
                        >
                          <i class="ri-organization-chart" />
                          <span>{{ c.name }}</span>
                          <em>（{{ c.memberCount || 0 }} 人）</em>
                          <span v-if="showIncompleteTip(c)" class="tree__todo">待完善</span>
                        </button>
                      </li>
                      <li
                        v-if="!inspectMode && !visibleCommitteesOf(b).length"
                        class="tree__empty tree__empty--nested"
                      >
                        暂无专委会
                      </li>
                    </ul>
                  </li>
                  <li v-if="!visibleBranches.length" class="tree__empty">
                    暂无组织节点
                  </li>
                </ul>
              </li>
            </ul>
          </el-scrollbar>
        </section>
      </div>

      <div class="col col--right">
        <section class="panel panel--chart">
          <h3>近12月新会员增长</h3>
          <div ref="growthChart" class="chart"></div>
        </section>

        <section class="panel panel--reps">
          <h3>代表人士墙</h3>
          <div class="reps">
            <button
              v-for="(it, i) in reps"
              :key="it.id || i"
              type="button"
              class="reps__card"
              @click="openRep(it)"
            >
              <div class="reps__avatar" :style="avatarStyle(it)">
                <span v-if="!mediaOf(it)">{{ nameInitial(it) }}</span>
                <img
                  v-if="it.isPartyMember"
                  class="reps__party"
                  :src="partyEmblem"
                  alt="党员"
                  title="中共党员"
                />
              </div>
              <div class="reps__meta">
                <strong>
                  <span class="reps__name">{{ it.name || it.Name || "—" }}</span>
                  <span v-if="it.isNpc" class="reps__role reps__role--npc">人大</span>
                  <span v-if="it.isCppcc" class="reps__role reps__role--cppcc">政协</span>
                </strong>
                <em class="reps__title">{{ it.title || it.Title || "" }}</em>
              </div>
              <em class="reps__unit">{{ it.unit || it.Unit || "" }}</em>
            </button>
            <p v-if="!reps.length" class="empty">暂无代表人士</p>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<script>
import { PieChart, BarChart } from "@/plugins/echarts_bank/index";
import branchQuery from "@/utils/branchQuery";
import { absMediaUrl, toDetailItem, memberAvatarUrl, memberAvatarStyle, memberNameInitial } from "@/utils/mediaUrl";
import { isSameJson } from "@/utils/stableData";
import dashPage from "@/mixins/dashPage";
import partyEmblem from "@/assets/dash/party-emblem.png";

const POLITICS_COLORS = {
  中共党员: "#e74c3c",
  民主党派成员: "#3d8bff",
  无党派人士: "#f5c542",
  群众: "#36d7b7"
};

export default {
  name: "DashOrg",
  mixins: [branchQuery, dashPage],
  data() {
    return {
      payload: {},
      pie: null,
      bar: null,
      rootExpanded: true,
      expandedBranchIds: {},
      partyEmblem,
      politicsSig: null,
      growthSig: null
    };
  },
  computed: {
    kpis() {
      return this.payload.kpis || {};
    },
    orgName() {
      return this.payload.orgName || "新联会";
    },
    branches() {
      return this.payload.branches || [];
    },
    visibleBranches() {
      const list = this.branches;
      if (!this.inspectMode) return list;
      // 迎检：只保留有成员、有活动的健全分会（无 activityCount 字段时仅按成员）
      return list.filter(b => {
        if ((b.memberCount || 0) <= 0) return false;
        if (b.activityCount == null) return true;
        return Number(b.activityCount) > 0;
      });
    },
    reps() {
      return (this.payload.reps || []).slice(0, 12);
    },
    politicsList() {
      return this.payload.politics || [];
    }
  },
  watch: {
    branchQuery: {
      deep: true,
      handler() {
        this.load();
      }
    },
    branches: {
      immediate: true,
      handler(list) {
        // 默认全部展开（分会 + 专委会）
        const next = { ...this.expandedBranchIds };
        (list || []).forEach(b => {
          if (b && b.id != null && next[b.id] === undefined) {
            next[b.id] = true;
          }
        });
        this.expandedBranchIds = next;
      }
    },
    payload() {
      this.redrawIfChanged("politicsSig", this.payload.politics || [], () => {
        this.$nextTick(() => this.renderPolitics());
      });
      this.redrawIfChanged("growthSig", this.payload.growth || [], () => {
        this.$nextTick(() => this.renderGrowth());
      });
      const k = this.payload.kpis || {};
      this.syncRollKpis({
        members: k.members || 0,
        flow: k.flow || 0,
        newMembers: k.newMembers || 0,
        reps: k.reps || 0
      });
    }
  },
  mounted() {
    this.initCharts();
    this.load();
    this.startPageRefresh();
  },
  beforeDestroy() {
    if (this.pie) this.pie.destroy();
    if (this.bar) this.bar.destroy();
  },
  methods: {
    mediaOf(it) {
      return memberAvatarUrl(it);
    },
    avatarStyle(it) {
      return memberAvatarStyle(it);
    },
    nameInitial(it) {
      return memberNameInitial(it);
    },
    toggleRoot() {
      this.rootExpanded = !this.rootExpanded;
    },
    isBranchExpanded(id) {
      return !!this.expandedBranchIds[id];
    },
    toggleBranch(id) {
      this.$set(this.expandedBranchIds, id, !this.expandedBranchIds[id]);
    },
    visibleCommitteesOf(branch) {
      const list = (branch && branch.committees) || [];
      if (!this.inspectMode) return list;
      return list.filter(c => (c.memberCount || 0) > 0);
    },
    isEmptyNode(node) {
      return !node || !(node.memberCount || 0);
    },
    showIncompleteTip(node) {
      return !this.inspectMode && this.isEmptyNode(node);
    },
    initCharts() {
      const fs = this.commonFont;
      const self = this;
      this.pie = new PieChart({
        ele: this.$refs.politicsChart,
        fontSize: fs,
        palletColors: ["#e74c3c", "#3d8bff", "#f5c542", "#36d7b7"],
        legend: {
          show: true,
          orient: "vertical",
          left: "58%",
          top: "middle",
          textStyle: { color: "#cfe3ff", fontSize: Math.max(11, fs - 1) },
          formatter(name) {
            const row = self.politicsList.find(x => x.name === name);
            if (!row) return name;
            const total =
              self.politicsList.reduce((s, x) => s + (Number(x.value) || 0), 0) ||
              1;
            const pct = Math.round((100 * (Number(row.value) || 0)) / total);
            return `${name}  ${pct}%`;
          }
        },
        tooltip: {
          show: true,
          trigger: "item",
          formatter(res) {
            return `${res.name}：${res.value} 人，占比 ${Math.round(
              res.percent || 0
            )}%`;
          }
        },
        series: {
          name: "政治面貌",
          center: ["30%", "50%"],
          radius: ["42%", "68%"],
          label: { show: false },
          labelLine: { show: false }
        }
      });
      this.bar = new BarChart({
        ele: this.$refs.growthChart,
        fontSize: fs,
        palletColors: [["#7ae7ff", "#1a6dff"]],
        mAxis: "xAxis",
        vAxis: "yAxis",
        grid: { top: 2.2 * fs, right: fs, bottom: 0.5 * fs, left: 0.5 * fs },
        series: [
          {
            type: "bar",
            name: "新增",
            barWidth: "42%",
            label: {
              show: true,
              position: "top",
              fontSize: Math.max(10, fs - 2),
              color: "#e8f4ff"
            }
          }
        ]
      });
    },
    async load() {
      const res = await this.$api("screen", "org", this.branchQuery);
      if (res && res.code === 0) {
        if (isSameJson(this.payload, res)) return;
        this.payload = res;
      }
    },
    renderPolitics() {
      if (!this.pie) return;
      const raw = this.politicsList;
      const data = (raw.length
        ? raw
        : [
            { name: "中共党员", value: 0 },
            { name: "民主党派成员", value: 0 },
            { name: "无党派人士", value: 0 },
            { name: "群众", value: 0 }
          ]
      ).map(x => ({
        name: x.name,
        value: Number(x.value) || 0,
        itemStyle: { color: POLITICS_COLORS[x.name] || undefined }
      }));
      const hasAny = data.some(d => d.value > 0);
      this.pie
        .setData(hasAny ? data : [{ name: "暂无", value: 1 }])
        .draw();
      this.bindPieEvents();
    },
    bindPieEvents() {
      const chart = this.pie && this.pie._chart;
      if (!chart) return;
      chart.off("click");
      chart.on("click", params => {
        if (!params || params.componentType !== "series") return;
        if (!params.name || params.name === "暂无") return;
        this.openPolitics(params.name, params.value, params.percent);
      });
    },
    renderGrowth() {
      if (!this.bar) return;
      const growth = this.payload.growth || [];
      const data = growth.map(x => ({
        name: x.name,
        value: Number(x.value) || 0
      }));
      this.bar
        .setData([data.length ? data : [{ name: "-", value: 0 }]])
        .draw();
      this.bindBarEvents();
    },
    bindBarEvents() {
      const chart = this.bar && this.bar._chart;
      if (!chart) return;
      chart.off("click");
      chart.on("click", params => {
        if (!params || params.componentType !== "series") return;
        const growth = this.payload.growth || [];
        const row = growth[params.dataIndex];
        if (!row) return;
        this.openGrowthMonth(row);
      });
    },
    async openGrowthMonth(row) {
      const month = row.month || "";
      const label = month || row.name || "当月";
      const pageSize = 20;
      const mapItem = m =>
        toDetailItem({
          title: m.name,
          content: [m.unit, m.title].filter(Boolean).join(" · ") || "新会员",
          avatarUrl: m.avatarUrl,
          time: m.joinDate,
          author: m.unit,
          category: m.title || "新会员",
          meta: [m.unit, m.title].filter(Boolean).join(" · ")
        });
      const fetchPage = async (skip, take) => {
        const res = await this.$api("screen", "orgMembers", {
          month,
          skip,
          take,
          ...this.branchQuery
        });
        if (!res || res.code !== 0) {
          return { items: [], hasMore: false, total: row.value || 0 };
        }
        return {
          items: (res.items || []).map(mapItem),
          hasMore: !!res.hasMore,
          total: res.total != null ? res.total : row.value || 0
        };
      };
      let first;
      try {
        first = await fetchPage(0, pageSize);
      } catch (e) {
        first = { items: [], hasMore: false, total: row.value || 0 };
      }
      this.openDrawer({
        title: `${label} 新入会会员`,
        summary: [`当月新增：${first.total} 人`],
        items: first.items.length
          ? first.items
          : [
              toDetailItem({
                title: "当月暂无新会员",
                content: "请核对会员管理中的入会日期"
              })
            ],
        hasMore: first.items.length ? first.hasMore : false,
        infinite: first.items.length
          ? {
              pageSize,
              fetch: fetchPage
            }
          : null
      });
    },
    openMembersKpi() {
      this.openOrgMemberDrawer({
        type: "all",
        title: "会员名册",
        summaryOf: total => [`在册会员：${total} 人`],
        mapItem: m =>
          toDetailItem({
            title: m.name,
            content: [m.title, m.unit, m.stratumType, m.politicalStatus]
              .filter(Boolean)
              .join(" · "),
            avatarUrl: m.avatarUrl,
            meta: m.isRepresentative ? "代表人士" : m.isFlowPartyMember ? "流动党员" : "",
            category: m.stratumType,
            time: m.joinDate
          }),
        searchable: true
      });
    },
    openFlowKpi() {
      this.openOrgMemberDrawer({
        type: "flow",
        title: "流动党员 · 双报到台账",
        summaryOf: total => [`流动党员：${total} 人`],
        mapItem: m =>
          toDetailItem({
            title: m.name,
            content: [
              m.unit,
              m.title,
              `报到时间：${m.reportTime || "—"}`,
              `参加组织生活：${m.orgLifeCount != null ? m.orgLifeCount : 0} 次`
            ]
              .filter(Boolean)
              .join(" · "),
            avatarUrl: m.avatarUrl,
            time: m.reportTime,
            status: `组织生活 ${m.orgLifeCount != null ? m.orgLifeCount : 0} 次`,
            category: "流动党员"
          })
      });
    },
    openNewMembersKpi() {
      this.openOrgMemberDrawer({
        type: "new",
        title: "年新会员",
        summaryOf: total => [`本年新发展：${total} 人`],
        mapItem: m =>
          toDetailItem({
            title: m.name,
            content: [m.unit, m.title].filter(Boolean).join(" · ") || "新会员",
            avatarUrl: m.avatarUrl,
            time: m.joinDate,
            author: m.unit,
            category: "新会员"
          })
      });
    },
    openRepsKpi() {
      this.openOrgMemberDrawer({
        type: "reps",
        title: "代表人士名单",
        summaryOf: total => [`代表人士：${total} 人（按层级从高到低）`],
        mapItem: m =>
          toDetailItem({
            title: m.name,
            content: [m.title, m.unit].filter(Boolean).join(" · "),
            avatarUrl: m.avatarUrl,
            category: m.title,
            meta: m.unit
          })
      });
    },
    async openOrgMemberDrawer({ type, title, summaryOf, mapItem, searchable }) {
      const pageSize = 20;
      const fetchPage = async (skip, take, keyword) => {
        const res = await this.$api("screen", "orgMembers", {
          type,
          skip,
          take,
          keyword: keyword || "",
          ...this.branchQuery
        });
        if (!res || res.code !== 0) {
          return { items: [], hasMore: false, total: 0, summary: summaryOf(0) };
        }
        const total = res.total != null ? res.total : 0;
        return {
          items: (res.items || []).map(mapItem),
          hasMore: !!res.hasMore,
          total,
          summary: summaryOf(total)
        };
      };
      let first;
      try {
        first = await fetchPage(0, pageSize, "");
      } catch (e) {
        first = { items: [], hasMore: false, total: 0, summary: summaryOf(0) };
      }
      this.openDrawer({
        title,
        summary: first.summary,
        items: first.items.length
          ? first.items
          : searchable
            ? []
            : [
                toDetailItem({
                  title: "暂无名单",
                  content: "请在后台「会员管理」维护相关字段"
                })
              ],
        hasMore: !!first.hasMore,
        searchable: !!searchable,
        infinite: {
          pageSize,
          fetch: fetchPage
        }
      });
    },
    async openPolitics(name, value, percent) {
      const pct = Math.round(percent || 0);
      const pageSize = 20;
      const mapItem = m =>
        toDetailItem({
          title: m.name,
          content: [m.title, m.unit, m.stratumType].filter(Boolean).join(" · ") || "会员",
          avatarUrl: m.avatarUrl,
          category: name,
          time: m.joinDate,
          status: m.isFlowPartyMember ? "流动党员" : "",
          meta: m.isFlowPartyMember ? "流动党员" : m.isRepresentative ? "代表人士" : ""
        });
      const fetchPage = async (skip, take) => {
        const res = await this.$api("screen", "politicsMembers", {
          name,
          skip,
          take,
          ...this.branchQuery
        });
        if (!res || res.code !== 0) {
          return {
            items: [],
            hasMore: false,
            total: value || 0,
            flowCount: 0
          };
        }
        return {
          items: (res.items || []).map(mapItem),
          hasMore: !!res.hasMore,
          total: res.total != null ? res.total : value || 0,
          flowCount: res.flowCount || 0
        };
      };
      let first;
      try {
        first = await fetchPage(0, pageSize);
      } catch (e) {
        first = { items: [], hasMore: false, total: value || 0, flowCount: 0 };
      }
      this.openDrawer({
        title: `${name} · 会员名单`,
        summary: [
          `本类人数：${first.total} 人`,
          `占比：${pct}%`,
          `其中流动党员：${first.flowCount} 人`
        ],
        items: first.items.length
          ? first.items
          : [
              toDetailItem({
                title: "暂无会员",
                content: "请在会员管理中维护政治面貌"
              })
            ],
        hasMore: first.items.length ? first.hasMore : false,
        infinite: first.items.length
          ? {
              pageSize,
              fetch: fetchPage
            }
          : null
      });
    },
    async openCommittee(c, branch) {
      const name = (c && (c.name || c.Name)) || "专委会";
      const summary = (c && (c.summary || c.Summary)) || "专委会简介待补充";
      const count = (c && c.memberCount) || 0;
      const pageSize = 20;
      const mapItem = m =>
        toDetailItem({
          title: m.name,
          content: [m.title, m.unit, m.stratumType].filter(Boolean).join(" · ") || "会员",
          avatarUrl: m.avatarUrl,
          category: name,
          time: m.joinDate,
          meta: m.isRepresentative ? "代表人士" : ""
        });
      const fetchPage = async (skip, take) => {
        const res = await this.$api("screen", "orgMembers", {
          type: "all",
          committeeId: c.id,
          skip,
          take,
          ...(branch && branch.id != null ? { branchId: branch.id } : this.branchQuery)
        });
        if (!res || res.code !== 0) {
          return { items: [], hasMore: false, total: count };
        }
        return {
          items: (res.items || []).map(mapItem),
          hasMore: !!res.hasMore,
          total: res.total != null ? res.total : count
        };
      };
      let first;
      try {
        first = await fetchPage(0, pageSize);
      } catch (e) {
        first = { items: [], hasMore: false, total: count };
      }
      this.openDrawer({
        title: name,
        summary: [
          summary,
          branch && branch.name ? `所属分会：${branch.name}` : "",
          `在册人数：${first.total} 人`
        ].filter(Boolean),
        items: first.items.length
          ? first.items
          : [
              toDetailItem({
                title: "暂无挂靠会员",
                content: "请在会员管理中为会员勾选专委会"
              })
            ],
        hasMore: first.items.length ? first.hasMore : false,
        infinite: first.items.length
          ? {
              pageSize,
              fetch: fetchPage
            }
          : null
      });
    },
    onBranchClick(b) {
      if (!b || b.id == null) return;
      this.$router.push({ path: "/overview", query: { branchId: b.id } });
    },
    async openRep(it) {
      const memberId = (it && (it.id || it.Id)) || null;
      const name = (it && (it.name || it.Name)) || "代表人士";
      let profile = null;
      try {
        const res = await this.$api("screen", "repProfile", {
          ...(memberId ? { memberId } : { name }),
          ...this.branchQuery
        });
        if (res && res.code === 0) profile = res;
      } catch (e) {
        profile = null;
      }

      const year = (profile && profile.year) || new Date().getFullYear();
      const title = (profile && profile.title) || it.title || it.Title || "";
      const unit = (profile && profile.unit) || it.unit || it.Unit || "";
      const pol = (profile && profile.politicalStatus) || "";
      const activityCount = (profile && profile.activityCount) || 0;
      const suggestionCount = (profile && profile.suggestionCount) || 0;
      const volunteerHours = (profile && profile.volunteerHours) || 0;
      const enterpriseCount = (profile && profile.enterpriseCount) || 0;

      const badgeBits = [];
      if ((profile && profile.isPartyMember) || it.isPartyMember) badgeBits.push("中共党员");
      if ((profile && profile.isNpc) || it.isNpc) badgeBits.push("人大代表");
      if ((profile && profile.isCppcc) || it.isCppcc) badgeBits.push("政协委员");
      if ((profile && profile.isFlowPartyMember) || it.isFlowPartyMember) {
        badgeBits.push("流动党员");
      }

      const emptyHint = "暂无关联记录。请在对应台账中选择该会员，即可自动汇总。";
      const fmtLines = (rows, mapFn) => (rows || []).map(mapFn).filter(Boolean);

      const activityLines = fmtLines(profile && profile.activities, a =>
        `· ${a.time || ""}  ${a.title || ""}${a.category ? `（${a.category}）` : ""}`.trim()
      );
      const suggestionLines = fmtLines(profile && profile.suggestions, s =>
        `· ${s.time || ""}  ${s.title || ""}${s.status ? `〔${s.status}〕` : ""}`.trim()
      );
      const volunteerLines = fmtLines(profile && profile.volunteers, v =>
        `· ${v.time || ""}  ${v.title || ""}（${v.hours != null ? v.hours : 0} 小时）`.trim()
      );
      const enterpriseLines = fmtLines(profile && profile.enterprises, s => {
        const bits = [
          s.time || "",
          s.title || "",
          s.category ? `〔${s.category}〕` : "",
          s.status ? `〔${s.status}〕` : ""
        ].filter(Boolean);
        return bits.length ? `· ${bits.join("  ")}` : "";
      });

      const baseInfo = [
        title ? `职务：${title}` : "",
        unit ? `单位：${unit}` : "",
        pol ? `政治面貌：${pol}` : ""
      ]
        .filter(Boolean)
        .join("\n");

      const items = [
        toDetailItem({
          title: name,
          category: "基本信息",
          time: `${year}年度`,
          author: unit || "代表人士",
          status: badgeBits[0] || "",
          avatarUrl: (profile && profile.avatarUrl) || it.avatarUrl || it.AvatarUrl,
          content: baseInfo
        }),
        toDetailItem({
          title: "参加活动",
          category: "活动",
          status: `${activityCount} 次`,
          time: `${year}年`,
          content: activityLines.length ? activityLines.join("\n") : emptyHint
        }),
        toDetailItem({
          title: "建言献策",
          category: "建言",
          status: `${suggestionCount} 条`,
          time: `${year}年`,
          content: suggestionLines.length ? suggestionLines.join("\n") : emptyHint
        }),
        toDetailItem({
          title: "公益服务",
          category: "公益",
          status: `${volunteerHours} 小时`,
          time: `${year}年`,
          content: volunteerLines.length
            ? volunteerLines.join("\n")
            : emptyHint
        }),
        toDetailItem({
          title: "三单与助企",
          category: "三单与助企",
          status: `${enterpriseCount} 次`,
          time: `${year}年`,
          content: enterpriseLines.length ? enterpriseLines.join("\n") : emptyHint
        })
      ];

      this.openDrawer({
        title: `${name} · 履职档案`,
        summary: [
          `活动 ${activityCount} 次`,
          `建言 ${suggestionCount} 条`,
          `公益 ${volunteerHours} 小时`,
          `三单与助企 ${enterpriseCount} 次`
        ],
        items
      });
    }
  }
};
</script>

<style lang="less" scoped>
.page {
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 10 / @rem2;
  min-height: 0;
}
.kpi-row {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 10 / @rem2;
}
.kpi {
  min-height: 80 / @rem2;
  box-sizing: border-box;
  padding: 12 / @rem2 14 / @rem2;
  border: 1 / @rem2 solid fade(@mainColor, 35%);
  border-radius: 6 / @rem2;
  background: rgba(8, 28, 64, 0.55);
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10 / @rem2;
  cursor: pointer;
  &__left {
    display: flex;
    align-items: center;
    gap: 10 / @rem2;
    min-width: 0;
  }
  &__icon-wrap {
    position: relative;
    width: 36 / @rem2;
    height: 36 / @rem2;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    &::before {
      content: "";
      position: absolute;
      inset: 0;
      border-radius: 50%;
      background: radial-gradient(
        circle,
        fade(@mainColor, 32%) 0%,
        fade(@mainColor, 8%) 50%,
        transparent 72%
      );
    }
  }
  &__icon {
    position: relative;
    z-index: 1;
    font-size: 20 / @rem2;
    line-height: 1;
    color: #7ae7ff;
    filter: drop-shadow(0 0 4px fade(@mainColor, 65%));
  }
  &__icon-wrap--party {
    &::before {
      background: radial-gradient(
        circle,
        fade(#ed2c25, 40%) 0%,
        fade(#ed2c25, 10%) 52%,
        transparent 74%
      );
    }
  }
  &__party {
    position: relative;
    z-index: 1;
    width: 22 / @rem2;
    height: 22 / @rem2;
    display: block;
    object-fit: contain;
  }
  &__label {
    color: @assistColor;
    font-size: 14 / @rem2;
    white-space: nowrap;
  }
  b {
    font-size: 26 / @rem2;
    color: #fff;
    flex-shrink: 0;
    em {
      font-style: normal;
      font-size: 12 / @rem2;
      margin-left: 4 / @rem2;
      color: fade(#fff, 55%);
    }
  }
}
.cols {
  flex: 1;
  min-height: 0;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10 / @rem2;
}
.col {
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 10 / @rem2;
}
.panel {
  padding: 12 / @rem2;
  border: 1 / @rem2 solid fade(@assistColor, 22%);
  border-radius: 6 / @rem2;
  background: rgba(6, 22, 52, 0.55);
  display: flex;
  flex-direction: column;
  min-height: 0;
  h3 {
    margin: 0 0 10 / @rem2;
    color: @mainColor;
    font-size: 16 / @rem2;
  }
}
.panel--chart {
  flex: 1.1;
}
.panel--tree,
.panel--reps {
  flex: 1 1 0;
  min-height: 0;
  overflow: hidden;
}
.chart {
  flex: 1;
  min-height: 160 / @rem2;
}
.tree-scroll {
  flex: 1;
  min-height: 0;
  height: 0;
  ::v-deep .el-scrollbar__wrap {
    overflow-x: hidden;
  }
  ::v-deep .el-scrollbar__bar {
    &.is-vertical {
      width: 6px;
      right: 2px;
    }
    &.is-horizontal {
      display: none;
    }
  }
  ::v-deep .el-scrollbar__thumb {
    background: rgba(100, 180, 255, 0.35);
    border-radius: 4px;
    &:hover {
      background: rgba(100, 180, 255, 0.55);
    }
  }
}
.tree {
  margin: 0;
  padding: 0 8 / @rem2 0 0;
  list-style: none;
  &__children {
    margin: 0;
    padding: 0 0 0 18 / @rem2;
    list-style: none;
  }
  &__children--committee {
    padding-left: 28 / @rem2;
  }
  &__branch-row {
    display: flex;
    align-items: center;
    gap: 2 / @rem2;
  }
  &__toggle {
    flex: 0 0 auto;
    border: 0;
    background: transparent;
    color: @mainColor;
    padding: 4 / @rem2;
    cursor: pointer;
    line-height: 1;
    i {
      font-size: 16 / @rem2;
    }
  }
  &__node {
    display: flex;
    align-items: center;
    gap: 6 / @rem2;
    width: 100%;
    border: 0;
    background: transparent;
    color: #e8f3ff;
    text-align: left;
    padding: 7 / @rem2 4 / @rem2;
    cursor: pointer;
    font-size: 14 / @rem2;
    i {
      color: @mainColor;
      font-size: 16 / @rem2;
    }
    strong {
      font-size: 15 / @rem2;
    }
    em {
      font-style: normal;
      color: rgba(200, 220, 255, 0.75);
      font-size: 12 / @rem2;
    }
  }
  &__node--branch {
    flex: 1;
    min-width: 0;
  }
  &__node--committee {
    opacity: 0.95;
    font-size: 13 / @rem2;
  }
  &__node.is-incomplete {
    em {
      color: #ff8a3d;
    }
  }
  &__todo {
    flex-shrink: 0;
    margin-left: 4 / @rem2;
    padding: 1 / @rem2 6 / @rem2;
    border-radius: 3 / @rem2;
    font-size: 11 / @rem2;
    line-height: 1.4;
    color: #ffb070;
    background: rgba(255, 138, 61, 0.14);
    border: 1 / @rem2 solid rgba(255, 138, 61, 0.4);
  }
  &__empty {
    color: rgba(200, 220, 255, 0.55);
    padding: 8 / @rem2 4 / @rem2;
    font-size: 13 / @rem2;
  }
  &__empty--nested {
    padding-left: 8 / @rem2;
  }
  &__node--branch em {
    color: fade(@mainColor, 85%);
  }
  &__node:hover {
    color: #fff;
  }
}
.panel--reps {
  h3 {
    display: flex;
    align-items: baseline;
    gap: 8 / @rem2;
  }
}
.panel__sub {
  font-size: 12 / @rem2;
  font-style: normal;
  font-weight: 400;
  color: fade(#9fd4ff, 75%);
}
.reps {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-template-rows: repeat(6, minmax(0, 1fr));
  gap: 6 / @rem2;
  overflow: hidden;
  /* flex-basis:0 + height:0 让父级分配的高度成为确定值，1fr 行才会真正撑满 */
  flex: 1 1 0;
  min-height: 0;
  height: 0;
  align-content: stretch;
  align-items: stretch;
}
.reps__card {
  position: relative;
  box-sizing: border-box;
  width: 100%;
  height: 100%;
  min-height: 0;
  display: flex;
  align-items: center;
  gap: 8 / @rem2;
  padding: 4 / @rem2 10 / @rem2;
  border: 1 / @rem2 solid fade(@assistColor, 28%);
  border-radius: 6 / @rem2;
  background: linear-gradient(
    90deg,
    rgba(12, 48, 96, 0.55),
    rgba(255, 255, 255, 0.04)
  );
  color: #e8f3ff;
  cursor: pointer;
  text-align: left;
  transition: border-color 0.2s ease, background 0.2s ease;
  &:hover {
    border-color: fade(@mainColor, 55%);
    background: linear-gradient(
      90deg,
      rgba(18, 70, 130, 0.7),
      rgba(255, 255, 255, 0.07)
    );
  }
}
.reps__avatar {
  position: relative;
  width: 34 / @rem2;
  height: 34 / @rem2;
  max-height: 72%;
  aspect-ratio: 1;
  border-radius: 50%;
  background: #0d3a6a center/cover no-repeat;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 13 / @rem2;
  color: #9fd4ff;
  box-shadow: 0 0 0 1 / @rem2 fade(@mainColor, 35%);
}
.reps__party {
  position: absolute;
  left: -3 / @rem2;
  top: -3 / @rem2;
  width: 13 / @rem2;
  height: 13 / @rem2;
  border-radius: 50%;
  box-shadow: 0 0 0 1px rgba(8, 28, 64, 0.9);
  pointer-events: none;
}
.reps__meta {
  min-width: 0;
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 4 / @rem2;
  strong {
    display: flex;
    align-items: center;
    flex-wrap: nowrap;
    gap: 5 / @rem2;
    font-size: 12 / @rem2;
    line-height: 1.15;
    min-width: 0;
  }
  .reps__name {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .reps__role {
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 13 / @rem2;
    padding: 0 4 / @rem2;
    border-radius: 3 / @rem2;
    font-size: 10 / @rem2;
    font-weight: 700;
    color: #fff;
    letter-spacing: 0.5px;
  }
  .reps__role--npc {
    background: linear-gradient(135deg, #b71c1c, #e53935);
  }
  .reps__role--cppcc {
    background: linear-gradient(135deg, #0d47a1, #1e88e5);
  }
  .reps__title {
    display: block;
    font-style: normal;
    font-size: 10 / @rem2;
    line-height: 1.2;
    color: fade(#fff, 55%);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}
.reps__unit {
  flex: 0 1 42%;
  max-width: 46%;
  min-width: 0;
  margin: 0;
  padding-left: 8 / @rem2;
  font-style: normal;
  font-size: 10 / @rem2;
  line-height: 1.25;
  color: fade(#fff, 55%);
  text-align: right;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.empty {
  color: fade(#fff, 45%);
  margin: 0;
  grid-column: 1 / -1;
}
</style>
